import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getApiUrl } from '../../config';
import { getAdminToken, clearAdminToken } from '../adminAuth';
import './AdminDashboard.css';
import InsightCard from './InsightCard';
import LlmModelSettings from './LlmModelSettings';

// 세션 진행 상태 컬럼 (key 는 백엔드 세션 필드 / 정렬 키, `${key}_time` 은 최초 기록 시각)
const SESSION_STATUS_COLUMNS = [
  { key: 'prayer_created', label: '기도', description: '기도 내용 입력' },
  { key: 'scripture_reached', label: '성경말씀', description: '성경말씀 단계까지 진행' },
  { key: 'worship_completed', label: '예배', description: '설교말씀 단계까지 진행' },
  { key: 'blessing_reached', label: '축도', description: '축도 단계까지 진행' },
  { key: 'donation_attempted', label: '후원 시도', description: '후원금 결제창 오픈' },
  { key: 'donation_made', label: '후원 결제', description: '후원금 최종 결제 완료' }
];

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [sessionsPagination, setSessionsPagination] = useState({});
  const [tokenUsage, setTokenUsage] = useState([]);
  const [insights, setInsights] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [adminToken] = useState(getAdminToken());
  const [authenticated, setAuthenticated] = useState(false);
  const [error, setError] = useState('');
  
  // Daily Worship states
  const [selectedLanguage, setSelectedLanguage] = useState('ko');
  const [customIssueTitle, setCustomIssueTitle] = useState('');
  const [customIssueDescription, setCustomIssueDescription] = useState('');
  const [worshipGenerating, setWorshipGenerating] = useState(false);
  const [worshipResult, setWorshipResult] = useState(null);
  const [schedulerStatus, setSchedulerStatus] = useState(null);
  const [supportedLanguages, setSupportedLanguages] = useState([]);
  const [selectedSchedulerLanguages, setSelectedSchedulerLanguages] = useState([]);
  const [permanentLanguages, setPermanentLanguages] = useState([]);
  const [languageSettingsSource, setLanguageSettingsSource] = useState('config');
  const [savingSettings, setSavingSettings] = useState(false);
  
  // Session deletion states
  const [selectedSessions, setSelectedSessions] = useState(new Set());
  const [deletingSession, setDeletingSession] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Donation payment history states
  const [donations, setDonations] = useState([]);
  const [donationsPagination, setDonationsPagination] = useState({});
  const [donationSummary, setDonationSummary] = useState(null);
  const [donationFilters, setDonationFilters] = useState({ status: '', paymentMethod: '' });

  // Security event (abnormal input attempt) states
  const [securityEvents, setSecurityEvents] = useState([]);
  const [securityFilters, setSecurityFilters] = useState({ severity: '', ip: '' });
  const [permanentBlockForm, setPermanentBlockForm] = useState({ ip: '', reason: '' });
  const [permanentBlockedIps, setPermanentBlockedIps] = useState([]);
  const [blockingIp, setBlockingIp] = useState(null);
  
  // Global date range for all analytics
  const [globalDateRange, setGlobalDateRange] = useState({
    preset: '1month', // default to 1 month
    startDate: '',
    endDate: '',
    customMode: false
  });

  // Session filtering and sorting states (removed date filters)
  const [sessionFilters, setSessionFilters] = useState({
    sortBy: 'access_time',
    sortOrder: 'desc',
    currentPage: 1
  });

  // Use configured API base path to support dev/prod seamlessly
  // Pray4Me app uses pray4me-admin prefix
  const API_BASE = getApiUrl('pray4me-admin');

  const sanitizeHeaderValue = (value) => {
    if (typeof value !== 'string') return '';
    try {
      // Normalize and strip characters outside ISO-8859-1 range
      // eslint-disable-next-line no-control-regex
      return value.normalize('NFKC').replace(/[^\x00-\xff]/g, '').trim();
    } catch (e) {
      // eslint-disable-next-line no-control-regex
      return (value || '').replace(/[^\x00-\xff]/g, '').trim();
    }
  };

  const safeToken = sanitizeHeaderValue(adminToken);

  const authHeaders = {
    'Authorization': `Bearer ${safeToken}`,
    'Content-Type': 'application/json'
  };

  // 토큰이 없거나 만료된 경우 로그인 페이지로 이동
  const redirectToLogin = () => {
    clearAdminToken();
    setAuthenticated(false);
    navigate('/admin/login', { replace: true });
  };

  // Date range presets
  const datePresets = {
    '24hours': { label: '최근 24시간', hours: 24 },
    '48hours': { label: '최근 48시간', hours: 48 },
    '1week': { label: '최근 1주일', days: 7 },
    '1month': { label: '최근 1개월', days: 30 },
    '2months': { label: '최근 2개월', days: 60 },
    '6months': { label: '최근 6개월', days: 180 },
    '1year': { label: '최근 1년', days: 365 }
  };

  // Calculate actual date range based on preset or custom dates
  const calculateDateRange = (preset, customStart, customEnd) => {
    if (preset === 'custom') {
      return {
        startDate: customStart,
        endDate: customEnd
      };
    }

    const now = new Date();
    const presetConfig = datePresets[preset];
    
    if (!presetConfig) {
      // Fallback to 1 month
      const startDate = new Date(now.getTime() - (30 * 24 * 60 * 60 * 1000));
      return {
        startDate: startDate.toISOString(),
        endDate: now.toISOString()
      };
    }

    let startDate;
    if (presetConfig.hours) {
      startDate = new Date(now.getTime() - (presetConfig.hours * 60 * 60 * 1000));
    } else if (presetConfig.days) {
      startDate = new Date(now.getTime() - (presetConfig.days * 24 * 60 * 60 * 1000));
    }

    return {
      startDate: startDate.toISOString(),
      endDate: now.toISOString()
    };
  };

  // Get current effective date range
  const getEffectiveDateRange = () => {
    return calculateDateRange(
      globalDateRange.customMode ? 'custom' : globalDateRange.preset,
      globalDateRange.startDate,
      globalDateRange.endDate
    );
  };

  useEffect(() => {
    if (!adminToken) {
      navigate('/admin/login', { replace: true });
      return;
    }
    // localStorage에서 토큰을 읽어온 경우 유효성 검증
    validateToken();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminToken]);

  const validateToken = async () => {
    try {
      const response = await fetch(`${API_BASE}/health`, { headers: authHeaders });

      if (response.status === 401) {
        // 저장된 토큰이 유효하지 않거나 만료된 경우
        redirectToLogin();
        return;
      }

      if (response.ok) {
        setAuthenticated(true);
        fetchDashboardData();
        return;
      }

      setError('서버 연결에 실패했습니다.');
    } catch (error) {
      console.error('Admin token validation error:', error);
      setError('서버 연결에 실패했습니다.');
    }
  };

  const handleLogout = () => {
    setDashboardData(null);
    setSessions([]);
    setTokenUsage([]);
    setInsights([]);
    redirectToLogin();
  };

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const dateRange = getEffectiveDateRange();
      const params = new URLSearchParams({
        start_date: dateRange.startDate,
        end_date: dateRange.endDate
      });
      
      const response = await fetch(`${API_BASE}/dashboard/overview?${params.toString()}`, {
        headers: authHeaders
      });

      if (response.status === 401) {
        // 토큰 만료 등 인증 실패 시 로그인 페이지로 이동
        redirectToLogin();
        return;
      }

      const data = await response.json();
      
      if (data.success) {
        setDashboardData(data.data);
        setAuthenticated(true);
        setError('');
      } else {
        setError(data.error || '데이터를 불러오는데 실패했습니다.');
      }
    } catch (error) {
      setError('서버 연결에 실패했습니다.');
      console.error('Dashboard data fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSessions = async (page = 1, filters = sessionFilters) => {
    try {
      // Build query parameters with global date range
      const dateRange = getEffectiveDateRange();
      const params = new URLSearchParams({
        page: page.toString(),
        per_page: '20',
        start_date: dateRange.startDate,
        end_date: dateRange.endDate
      });
      
      if (filters.sortBy) {
        params.append('sort_by', filters.sortBy);
      }
      if (filters.sortOrder) {
        params.append('sort_order', filters.sortOrder);
      }
      
      const response = await fetch(`${API_BASE}/dashboard/sessions?${params.toString()}`, {
        headers: authHeaders
      });
      const data = await response.json();
      if (data.success) {
        setSessions(data.data.sessions);
        setSessionsPagination(data.data.pagination);
      } else {
        setError(data.error || '세션 데이터를 불러오는데 실패했습니다.');
      }
    } catch (error) {
      console.error('Sessions fetch error:', error);
      setError('세션 데이터를 불러오는 중 오류가 발생했습니다.');
    }
  };

  const fetchDonations = async (page = 1, filters = donationFilters) => {
    try {
      const dateRange = getEffectiveDateRange();
      const params = new URLSearchParams({
        page: page.toString(),
        per_page: '20',
        start_date: dateRange.startDate,
        end_date: dateRange.endDate
      });
      if (filters.status) params.append('status', filters.status);
      if (filters.paymentMethod) params.append('payment_method', filters.paymentMethod);

      const response = await fetch(`${API_BASE}/dashboard/donations?${params.toString()}`, {
        headers: authHeaders
      });
      if (response.status === 401) {
        redirectToLogin();
        return;
      }
      const data = await response.json();
      if (data.success) {
        setDonations(data.data.donations);
        setDonationsPagination(data.data.pagination);
        setDonationSummary(data.data.summary);
      } else {
        setError(data.error || '후원 결제 내역을 불러오는데 실패했습니다.');
      }
    } catch (error) {
      console.error('Donations fetch error:', error);
      setError('후원 결제 내역을 불러오는 중 오류가 발생했습니다.');
    }
  };

  const fetchSecurityEvents = async (filters = securityFilters) => {
    try {
      const params = new URLSearchParams({ limit: '200' });
      if (filters.severity) params.append('severity', filters.severity);
      if (filters.ip.trim()) params.append('ip', filters.ip.trim());

      const response = await fetch(`${API_BASE}/security/events?${params.toString()}`, {
        headers: authHeaders
      });
      if (response.status === 401) {
        redirectToLogin();
        return;
      }
      const data = await response.json();
      if (data.success) {
        setSecurityEvents(data.data.events);
      } else {
        setError(data.error || '보안 이벤트를 불러오는데 실패했습니다.');
      }
    } catch (error) {
      console.error('Security events fetch error:', error);
      setError('보안 이벤트를 불러오는 중 오류가 발생했습니다.');
    }
  };

  const fetchPermanentBlockedIps = async () => {
    try {
      const response = await fetch(`${API_BASE}/security/status`, { headers: authHeaders });
      if (response.status === 401) {
        redirectToLogin();
        return;
      }
      const data = await response.json();
      if (data.success) {
        setPermanentBlockedIps(data.data.blocked_ips.filter((blocked) => blocked.is_permanent));
      }
    } catch (error) {
      console.error('Blocked IPs fetch error:', error);
    }
  };

  const fetchSecurityData = (filters = securityFilters) => {
    fetchSecurityEvents(filters);
    fetchPermanentBlockedIps();
  };

  const permanentlyBlockIp = async (ipAddress, reason = '') => {
    const targetIp = (ipAddress || '').trim();
    if (!targetIp) {
      setError('영구 차단할 IP 주소를 입력해주세요.');
      return;
    }
    if (!window.confirm(`${targetIp} 을(를) 영구 차단하시겠습니까?\n해당 IP의 모든 접속이 차단됩니다.`)) {
      return;
    }

    setBlockingIp(targetIp);
    try {
      const response = await fetch(`${API_BASE}/security/permanent-block`, {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify({ ip_address: targetIp, reason })
      });
      if (response.status === 401) {
        redirectToLogin();
        return;
      }
      const data = await response.json();
      if (!data.success) {
        setError(data.error || 'IP 영구 차단에 실패했습니다.');
        return;
      }
      setError('');
      setPermanentBlockForm({ ip: '', reason: '' });
      fetchSecurityData(securityFilters);
    } catch (error) {
      console.error('Permanent block error:', error);
      setError('IP 영구 차단 중 오류가 발생했습니다.');
    } finally {
      setBlockingIp(null);
    }
  };

  const unblockIp = async (ipAddress) => {
    if (!window.confirm(`${ipAddress} 의 차단을 해제하시겠습니까?`)) {
      return;
    }
    try {
      const response = await fetch(`${API_BASE}/security/unblock-ip`, {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify({ ip_address: ipAddress, reason: 'Unblocked by admin (dashboard)' })
      });
      if (response.status === 401) {
        redirectToLogin();
        return;
      }
      const data = await response.json();
      if (!data.success) {
        setError(data.error || '차단 해제에 실패했습니다.');
        return;
      }
      setError('');
      fetchSecurityData(securityFilters);
    } catch (error) {
      console.error('Unblock IP error:', error);
      setError('차단 해제 중 오류가 발생했습니다.');
    }
  };

  const permanentlyBlockedIpSet = new Set(permanentBlockedIps.map((blocked) => blocked.ip_address));

  const securitySeverityLabels = {
    critical: '치명',
    high: '높음',
    medium: '중간',
    low: '낮음'
  };

  const securityActionLabels = {
    blocked: 'IP 차단',
    permanently_blocked: 'IP 영구 차단',
    rejected: '요청 거부',
    logged: '기록만'
  };

  const handleDonationFilterChange = (filterType, value) => {
    const newFilters = { ...donationFilters, [filterType]: value };
    setDonationFilters(newFilters);
    fetchDonations(1, newFilters);
  };

  const donationStatusLabels = {
    completed: '완료',
    pending: '대기',
    failed: '실패',
    canceled: '취소',
    refunded: '환불'
  };

  const paymentMethodLabels = {
    toss: '토스페이먼츠',
    eximbay: 'Eximbay',
    bank_transfer: '계좌이체'
  };

  const formatDonationAmount = (amount, currency = 'KRW') => {
    try {
      return new Intl.NumberFormat('ko-KR', { style: 'currency', currency }).format(amount || 0);
    } catch (e) {
      return `${formatNumber(amount || 0)} ${currency}`;
    }
  };

  const fetchTokenUsage = async () => {
    try {
      const dateRange = getEffectiveDateRange();
      const params = new URLSearchParams({
        start_date: dateRange.startDate,
        end_date: dateRange.endDate
      });
      
      const response = await fetch(`${API_BASE}/dashboard/token-usage?${params.toString()}`, {
        headers: authHeaders
      });
      const data = await response.json();
      if (data.success) {
        setTokenUsage(data.data.recent_usage);
      }
    } catch (error) {
      console.error('Token usage fetch error:', error);
    }
  };

  const fetchInsights = async () => {
    try {
      const dateRange = getEffectiveDateRange();
      const params = new URLSearchParams({
        limit: '10',
        start_date: dateRange.startDate,
        end_date: dateRange.endDate
      });
      
      const response = await fetch(`${API_BASE}/dashboard/insights?${params.toString()}`, {
        headers: authHeaders
      });
      const data = await response.json();
      if (data.success) {
        setInsights(data.data.insights);
      }
    } catch (error) {
      console.error('Insights fetch error:', error);
    }
  };

  const generateInsight = async (type) => {
    try {
      setLoading(true);
      const dateRange = getEffectiveDateRange();
      const response = await fetch(`${API_BASE}/dashboard/generate-insight`, {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify({
          type,
          start_date: dateRange.startDate,
          end_date: dateRange.endDate
        })
      });
      const data = await response.json();
      if (data.success) {
        await fetchInsights(); // Refresh insights list
        alert('인사이트가 성공적으로 생성되었습니다!');
      } else {
        alert('인사이트 생성에 실패했습니다: ' + data.error);
      }
    } catch (error) {
      alert('인사이트 생성 중 오류가 발생했습니다.');
      console.error('Generate insight error:', error);
    } finally {
      setLoading(false);
    }
  };

  const exportData = async (type, format = 'json') => {
    try {
      const dateRange = getEffectiveDateRange();
      const params = new URLSearchParams({
        type,
        format,
        start_date: dateRange.startDate,
        end_date: dateRange.endDate
      });
      
      const response = await fetch(`${API_BASE}/dashboard/export?${params.toString()}`, {
        headers: authHeaders
      });
      const data = await response.json();
      if (data.success) {
        // Download the data
        const blob = new Blob([JSON.stringify(data.data, null, 2)], { type: 'application/json' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${type}_export_${new Date().toISOString().split('T')[0]}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Export error:', error);
      alert('데이터 내보내기에 실패했습니다.');
    }
  };

  // Global date range handlers
  const handleDateRangeChange = (preset) => {
    if (preset === 'custom') {
      setGlobalDateRange({
        ...globalDateRange,
        preset: 'custom',
        customMode: true
      });
    } else {
      setGlobalDateRange({
        ...globalDateRange,
        preset: preset,
        customMode: false
      });
    }
  };

  // Refresh after the state update is committed so fetches use the newly selected preset
  useEffect(() => {
    if (!authenticated || globalDateRange.customMode) return;
    refreshAllData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globalDateRange.preset, globalDateRange.customMode]);

  // datetime-local values carry no timezone, so they are interpreted in the browser's local time
  const handleCustomDateChange = (type, localValue) => {
    const parsedDate = new Date(localValue);
    setGlobalDateRange({
      ...globalDateRange,
      [type]: isNaN(parsedDate.getTime()) ? '' : parsedDate.toISOString()
    });
  };

  const toLocalInputValue = (isoString) => {
    if (!isoString) return '';
    const date = new Date(isoString);
    if (isNaN(date.getTime())) return '';
    const pad = (num) => String(num).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
  };

  const applyCustomDateRange = () => {
    if (globalDateRange.startDate && globalDateRange.endDate) {
      refreshAllData();
    } else {
      setError('시작 날짜와 종료 날짜를 모두 입력해주세요.');
    }
  };

  const refreshAllData = () => {
    fetchDashboardData();
    if (activeTab === 'sessions') {
      fetchSessions(1, sessionFilters);
    } else if (activeTab === 'tokens') {
      fetchTokenUsage();
    } else if (activeTab === 'insights') {
      fetchInsights();
    } else if (activeTab === 'donations') {
      fetchDonations(1, donationFilters);
    } else if (activeTab === 'security') {
      fetchSecurityData(securityFilters);
    }
  };

  // Session filter handlers (simplified - no date filters)
  const handleFilterChange = (filterType, value) => {
    const newFilters = { ...sessionFilters, [filterType]: value, currentPage: 1 };
    setSessionFilters(newFilters);
  };

  const handleSort = (sortBy) => {
    const newSortOrder = sessionFilters.sortBy === sortBy && sessionFilters.sortOrder === 'desc' 
      ? 'asc' 
      : 'desc';
    const newFilters = { ...sessionFilters, sortBy, sortOrder: newSortOrder, currentPage: 1 };
    setSessionFilters(newFilters);
    fetchSessions(1, newFilters);
  };

  const handlePageChange = (page) => {
    const newFilters = { ...sessionFilters, currentPage: page };
    setSessionFilters(newFilters);
    fetchSessions(page, sessionFilters);
  };

  // Session deletion handlers
  const handleSessionSelect = (sessionId) => {
    const newSelected = new Set(selectedSessions);
    if (newSelected.has(sessionId)) {
      newSelected.delete(sessionId);
    } else {
      newSelected.add(sessionId);
    }
    setSelectedSessions(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedSessions.size === sessions.length) {
      setSelectedSessions(new Set());
    } else {
      setSelectedSessions(new Set(sessions.map(session => session._id)));
    }
  };

  const handleDeleteSession = async (sessionId) => {
    setDeletingSession(sessionId);
    try {
      const response = await fetch(`${API_BASE}/dashboard/sessions/${sessionId}`, {
        method: 'DELETE',
        headers: authHeaders
      });

      if (response.ok) {
        // Remove from local state
        setSessions(prev => prev.filter(session => session._id !== sessionId));
        setSelectedSessions(prev => {
          const newSet = new Set(prev);
          newSet.delete(sessionId);
          return newSet;
        });
        setError('');
      } else {
        const errorData = await response.json();
        setError(`삭제 실패: ${errorData.message || errorData.error}`);
      }
    } catch (err) {
      setError(`삭제 중 오류 발생: ${err.message}`);
    } finally {
      setDeletingSession(null);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedSessions.size === 0) {
      setError('삭제할 세션을 선택해주세요.');
      return;
    }

    setLoading(true);
    try {
      const sessionIds = Array.from(selectedSessions);
      const response = await fetch(`${API_BASE}/dashboard/sessions/bulk-delete`, {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify({ session_ids: sessionIds })
      });

      if (response.ok) {
        await response.json();
        // Remove deleted sessions from local state
        setSessions(prev => prev.filter(session => !selectedSessions.has(session._id)));
        setSelectedSessions(new Set());
        setError('');
        setShowDeleteConfirm(false);
        
        // Refresh sessions to get updated pagination
        await fetchSessions(1, sessionFilters);
      } else {
        const errorData = await response.json();
        setError(`일괄 삭제 실패: ${errorData.message || errorData.error}`);
      }
    } catch (err) {
      setError(`일괄 삭제 중 오류 발생: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('ko-KR', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 4
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString || dateString === 'Unknown') {
      return 'Unknown';
    }
    
    try {
      // If the date already comes formatted from the backend (with KST), just return it
      if (typeof dateString === 'string' && dateString.includes('KST')) {
        return dateString;
      }
      
      // Otherwise, try to parse and format the date
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return 'Invalid Date';
      }
      
      return date.toLocaleString('ko-KR', {
        timeZone: 'Asia/Seoul',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      });
    } catch (error) {
      console.error('Date formatting error:', error);
      return 'Invalid Date';
    }
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('ko-KR').format(num);
  };

  // Daily Worship Management Functions
  const languages = [
    { code: 'ko', name: '한국어 (Korean)' },
    { code: 'en', name: '영어 (English)' },
    { code: 'ja', name: '일본어 (Japanese)' },
    { code: 'zh', name: '중국어 (Chinese)' },
    { code: 'es', name: '스페인어 (Spanish)' },
    { code: 'fr', name: '프랑스어 (French)' },
    { code: 'ru', name: '러시아어 (Russian)' },
    { code: 'it', name: '이탈리아어 (Italian)' },
    { code: 'pt', name: '포르투갈어 (Portuguese)' },
    { code: 'id', name: '인도네시아어 (Indonesian)' },
    { code: 'vi', name: '베트남어 (Vietnamese)' },
    { code: 'th', name: '태국어 (Thai)' }
  ];

  const fetchSchedulerStatus = async () => {
    try {
      const response = await fetch(getApiUrl('daily-worship/scheduler/status'), {
        headers: authHeaders
      });
      const data = await response.json();
      if (data.success) {
        setSchedulerStatus(data.scheduler);
      }
    } catch (error) {
      console.error('Failed to fetch scheduler status:', error);
    }
  };

  const fetchSupportedLanguages = async () => {
    try {
      const response = await fetch(getApiUrl('daily-worship/supported-languages'));
      const data = await response.json();
      if (data.success) {
        setSupportedLanguages(data.languages_info);
      }
    } catch (error) {
      console.error('Failed to fetch supported languages:', error);
    }
  };

  const toggleSchedulerLanguage = (langCode) => {
    setSelectedSchedulerLanguages(prev => {
      if (prev.includes(langCode)) {
        return prev.filter(code => code !== langCode);
      } else {
        return [...prev, langCode];
      }
    });
  };

  const selectAllLanguages = () => {
    setSelectedSchedulerLanguages(supportedLanguages.map(lang => lang.code));
  };

  const deselectAllLanguages = () => {
    setSelectedSchedulerLanguages([]);
  };

  const fetchPermanentLanguageSettings = async () => {
    try {
      const response = await fetch(getApiUrl('daily-worship/settings/enabled-languages'), {
        headers: authHeaders
      });
      const data = await response.json();
      if (data.success) {
        setPermanentLanguages(data.enabled_languages || []);
        setLanguageSettingsSource(data.source || 'config');
      }
    } catch (error) {
      console.error('Failed to fetch permanent language settings:', error);
    }
  };

  const togglePermanentLanguage = (langCode) => {
    setPermanentLanguages(prev => {
      if (prev.includes(langCode)) {
        return prev.filter(code => code !== langCode);
      } else {
        return [...prev, langCode];
      }
    });
  };

  const selectAllPermanentLanguages = () => {
    setPermanentLanguages(supportedLanguages.map(lang => lang.code));
  };

  const deselectAllPermanentLanguages = () => {
    setPermanentLanguages([]);
  };

  const savePermanentLanguageSettings = async () => {
    if (permanentLanguages.length === 0) {
      setError('최소 1개 이상의 언어를 선택해주세요.');
      return;
    }

    setSavingSettings(true);
    setError('');
    setWorshipResult(null);

    try {
      const response = await fetch(getApiUrl('daily-worship/settings/enabled-languages'), {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify({ languages: permanentLanguages })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setWorshipResult({
          success: true,
          message: `✅ ${permanentLanguages.length}개 언어가 영구 설정으로 저장되었습니다!`
        });
        setLanguageSettingsSource('database');
        
        // 지원 언어 목록 새로고침
        fetchSupportedLanguages();
      } else {
        setError(data.error || '설정 저장에 실패했습니다.');
      }
    } catch (error) {
      setError('설정 저장 중 오류가 발생했습니다: ' + error.message);
    } finally {
      setSavingSettings(false);
    }
  };

  const resetPermanentLanguageSettings = async () => {
    if (!window.confirm('언어 설정을 초기화하시겠습니까? (config.py 기본값으로 돌아갑니다)')) {
      return;
    }

    setSavingSettings(true);
    setError('');
    setWorshipResult(null);

    try {
      const response = await fetch(getApiUrl('daily-worship/settings/enabled-languages'), {
        method: 'DELETE',
        headers: authHeaders
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setWorshipResult({
          success: true,
          message: '✅ 언어 설정이 초기화되었습니다. (config.py 기본값 사용)'
        });
        setLanguageSettingsSource('config');
        
        // 설정 새로고침
        fetchPermanentLanguageSettings();
        fetchSupportedLanguages();
      } else {
        setError(data.error || '설정 초기화에 실패했습니다.');
      }
    } catch (error) {
      setError('설정 초기화 중 오류가 발생했습니다: ' + error.message);
    } finally {
      setSavingSettings(false);
    }
  };

  const generateDailyWorship = async () => {
    setWorshipGenerating(true);
    setWorshipResult(null);
    setError('');

    try {
      const response = await fetch(getApiUrl('daily-worship/generate'), {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify({ language: selectedLanguage })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setWorshipResult({
          success: true,
          message: `${selectedLanguage} 예배가 성공적으로 생성되었습니다!`,
          worshipId: data.worship_id,
          issuesCount: data.issues_count
        });
      } else {
        setError(data.error || '예배 생성에 실패했습니다.');
      }
    } catch (error) {
      setError('예배 생성 중 오류가 발생했습니다: ' + error.message);
    } finally {
      setWorshipGenerating(false);
    }
  };

  const generateAllLanguages = async () => {
    if (!window.confirm('모든 언어(12개)의 예배를 생성합니다. 약 30-60분이 소요되고 비용이 발생합니다. 계속하시겠습니까?')) {
      return;
    }

    setWorshipGenerating(true);
    setWorshipResult(null);
    setError('');

    try {
      const response = await fetch(getApiUrl('daily-worship/generate-all'), {
        method: 'POST',
        headers: authHeaders
      });

      const data = await response.json();

      if (response.ok) {
        setWorshipResult({
          success: data.success,
          message: `전체 언어 생성 완료: ${data.success_count}/${data.total} 성공`,
          total: data.total,
          successCount: data.success_count,
          failedCount: data.failed_count,
          results: data.results
        });
      } else {
        setError(data.error || '전체 예배 생성에 실패했습니다.');
      }
    } catch (error) {
      setError('전체 예배 생성 중 오류가 발생했습니다: ' + error.message);
    } finally {
      setWorshipGenerating(false);
    }
  };

  const generateCustomWorship = async () => {
    if (!customIssueTitle || !customIssueDescription) {
      setError('이슈 제목과 설명을 모두 입력해주세요.');
      return;
    }

    setWorshipGenerating(true);
    setWorshipResult(null);
    setError('');

    try {
      const response = await fetch(getApiUrl('daily-worship/generate-custom'), {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify({
          language: selectedLanguage,
          issue_title: customIssueTitle,
          issue_description: customIssueDescription
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setWorshipResult({
          success: true,
          message: '사용자 정의 예배가 성공적으로 생성되었습니다!',
          worshipId: data.worship_id
        });
        setCustomIssueTitle('');
        setCustomIssueDescription('');
      } else {
        setError(data.error || '사용자 정의 예배 생성에 실패했습니다.');
      }
    } catch (error) {
      setError('사용자 정의 예배 생성 중 오류가 발생했습니다: ' + error.message);
    } finally {
      setWorshipGenerating(false);
    }
  };

  const triggerScheduler = async () => {
    const languagesToGenerate = selectedSchedulerLanguages.length > 0 
      ? selectedSchedulerLanguages 
      : null;

    const confirmMessage = languagesToGenerate
      ? `선택한 ${selectedSchedulerLanguages.length}개 언어의 예배를 생성합니다. 계속하시겠습니까?`
      : '활성화된 모든 언어의 예배가 생성됩니다. 계속하시겠습니까?';

    if (!window.confirm(confirmMessage)) {
      return;
    }

    setWorshipGenerating(true);
    setWorshipResult(null);
    setError('');

    try {
      const requestBody = languagesToGenerate 
        ? { languages: languagesToGenerate }
        : {};

      const response = await fetch(getApiUrl('daily-worship/scheduler/trigger'), {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify(requestBody)
      });

      const data = await response.json();

      if (response.ok) {
        setWorshipResult({
          success: data.success,
          message: languagesToGenerate
            ? `${selectedSchedulerLanguages.length}개 언어 생성 완료`
            : '스케줄러가 트리거되었습니다.',
          result: data.result
        });
      } else {
        setError(data.error || '스케줄러 트리거에 실패했습니다.');
      }
    } catch (error) {
      setError('스케줄러 트리거 중 오류가 발생했습니다: ' + error.message);
    } finally {
      setWorshipGenerating(false);
    }
  };

  // 인증 확인 중 (토큰 없음/만료 시 /admin/login 으로 이동됨)
  if (!authenticated) {
    return (
      <div className="admin-dashboard">
        {error
          ? <div className="error-message">{error}</div>
          : <div className="loading">인증 확인 중...</div>}
      </div>
    );
  }

  if (loading && !dashboardData) {
    return (
      <div className="admin-dashboard">
        <div className="loading">대시보드 데이터를 불러오는 중...</div>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      <div className="dashboard-header">
        <h1>📊 HeListensToMe 관리자 대시보드</h1>
        
        {/* Global Date Range Selector */}
        <div className="global-date-range">
          <div className="date-range-controls">
            <div className="preset-selector">
              <label>분석 기간:</label>
              <select 
                value={globalDateRange.customMode ? 'custom' : globalDateRange.preset}
                onChange={(e) => handleDateRangeChange(e.target.value)}
              >
                {Object.entries(datePresets).map(([key, preset]) => (
                  <option key={key} value={key}>{preset.label}</option>
                ))}
                <option value="custom">사용자 지정</option>
              </select>
            </div>
            
            {globalDateRange.customMode && (
              <div className="custom-date-inputs">
                <input
                  type="datetime-local"
                  value={toLocalInputValue(globalDateRange.startDate)}
                  onChange={(e) => handleCustomDateChange('startDate', e.target.value)}
                  placeholder="시작 날짜"
                />
                <span>~</span>
                <input
                  type="datetime-local"
                  value={toLocalInputValue(globalDateRange.endDate)}
                  onChange={(e) => handleCustomDateChange('endDate', e.target.value)}
                  placeholder="종료 날짜"
                />
                <button onClick={applyCustomDateRange} className="apply-custom-btn">
                  적용
                </button>
              </div>
            )}
          </div>
        </div>
        
        <div className="header-actions">
          <button onClick={() => refreshAllData()} disabled={loading} className="refresh-btn">
            🔄 새로고침
          </button>
          <button onClick={handleLogout} className="logout-btn">
            🚪 로그아웃
          </button>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="dashboard-tabs">
        <button 
          className={activeTab === 'overview' ? 'active' : ''} 
          onClick={() => setActiveTab('overview')}
        >
          📈 개요
        </button>
        <button 
          className={activeTab === 'sessions' ? 'active' : ''} 
          onClick={() => {
            setActiveTab('sessions');
            fetchSessions(1, sessionFilters);
          }}
        >
          👥 세션
        </button>
        <button 
          className={activeTab === 'tokens' ? 'active' : ''} 
          onClick={() => {
            setActiveTab('tokens');
            fetchTokenUsage();
          }}
        >
          🪙 토큰 사용량
        </button>
        <button 
          className={activeTab === 'donations' ? 'active' : ''} 
          onClick={() => {
            setActiveTab('donations');
            fetchDonations(1, donationFilters);
          }}
        >
          💳 후원 결제 내역
        </button>
        <button 
          className={activeTab === 'insights' ? 'active' : ''} 
          onClick={() => {
            setActiveTab('insights');
            fetchInsights();
          }}
        >
          🧠 AI 인사이트
        </button>
        <button 
          className={activeTab === 'security' ? 'active' : ''} 
          onClick={() => {
            setActiveTab('security');
            fetchSecurityData(securityFilters);
          }}
        >
          🛡️ 보안 이벤트
        </button>
        <button 
          className={activeTab === 'dailyWorship' ? 'active' : ''} 
          onClick={() => {
            setActiveTab('dailyWorship');
            fetchSchedulerStatus();
            fetchSupportedLanguages();
            fetchPermanentLanguageSettings();
          }}
        >
          📰 오늘의 예배
        </button>
        <button 
          className={activeTab === 'llmModels' ? 'active' : ''} 
          onClick={() => setActiveTab('llmModels')}
        >
          🤖 LLM 모델 관리
        </button>
      </div>

      <div className="dashboard-content">
        {activeTab === 'overview' && dashboardData && (
          <div className="overview-section">
            <div className="stats-grid">
              <div className="stat-card">
                <h3>총 세션</h3>
                <div className="stat-value">{formatNumber(dashboardData.overview.total_sessions || 0)}</div>
              </div>
              <div className="stat-card">
                <h3>고유 사용자</h3>
                <div className="stat-value">{formatNumber(dashboardData.overview.unique_users || 0)}</div>
              </div>
              <div className="stat-card">
                <h3>기도 요청</h3>
                <div className="stat-value">{formatNumber(dashboardData.overview.prayer_created_count || 0)}</div>
              </div>
              <div className="stat-card">
                <h3>예배 완료</h3>
                <div className="stat-value">{formatNumber(dashboardData.overview.worship_completed_count || 0)}</div>
              </div>
              <div className="stat-card">
                <h3>후원 건수</h3>
                <div className="stat-value">{formatNumber(dashboardData.overview.donation_made_count || 0)}</div>
              </div>
              <div className="stat-card">
                <h3>기도→예배 전환율</h3>
                <div className="stat-value">{(dashboardData.overview.prayer_to_worship_conversion_rate || 0).toFixed(1)}%</div>
              </div>
              <div className="stat-card">
                <h3>총 토큰 사용</h3>
                <div className="stat-value">{formatNumber(dashboardData.overview.total_tokens || 0)}</div>
              </div>
              <div className="stat-card">
                <h3>총 비용</h3>
                <div className="stat-value stat-value-compact">{formatCurrency(dashboardData.overview.total_cost || 0)}</div>
              </div>
            </div>

            <div className="charts-grid">
              <div className="chart-card">
                <h3>지역별 분포</h3>
                <div className="geographic-list">
                  {dashboardData.geographic_distribution?.slice(0, 5).map((item, index) => (
                    <div key={index} className="geo-item">
                      <span className="geo-location">{item.country} - {item.city}</span>
                      <span className="geo-count">{item.session_count}회</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="chart-card">
                <h3>언어별 사용 현황</h3>
                <div className="language-list">
                  {dashboardData.language_distribution?.slice(0, 5).map((item, index) => (
                    <div key={index} className="language-item">
                      <span className="language-name">{item.language}</span>
                      <span className="language-count">{item.session_count}회</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="chart-card">
                <h3>브라우저 분포</h3>
                <div className="browser-list">
                  {dashboardData.browser_distribution?.slice(0, 5).map((item, index) => (
                    <div key={index} className="browser-item">
                      <span className="browser-name">{item.browser}</span>
                      <span className="browser-count">{item.session_count}회</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="chart-card">
                <h3>AI 모델별 사용량</h3>
                <div className="model-list">
                  {dashboardData.model_usage?.slice(0, 5).map((item, index) => (
                    <div key={index} className="model-item">
                      <span className="model-name">{item.model}</span>
                      <span className="model-cost">{formatCurrency(item.total_cost)}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="chart-card">
                <h3>작업 유형별 분석</h3>
                <div className="operation-list">
                  {dashboardData.operation_breakdown?.slice(0, 5).map((item, index) => (
                    <div key={index} className="operation-item">
                      <span className="operation-name">{item.operation}</span>
                      <span className="operation-count">{item.usage_count}회</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="chart-card">
                <h3>오늘의 예배 현황</h3>
                <div className="daily-worship-stats">
                  <p>총 예배 수: {formatNumber(dashboardData.overview.daily_worship_count || 0)}</p>
                  <p>생성된 이슈: {formatNumber(dashboardData.overview.issues_generated_count || 0)}</p>
                  <div className="language-breakdown">
                    {dashboardData.daily_worship_by_language?.slice(0, 3).map((item, index) => (
                      <div key={index} className="lang-stat">
                        <span>{item.language}: {item.worship_count}개</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'sessions' && (
          <div className="sessions-section">
            <div className="section-header">
              <h3>사용자 세션 분석</h3>
              <button onClick={() => exportData('sessions')} className="export-btn">
                📥 내보내기
              </button>
            </div>
            
            {/* Sort Section */}
            <div className="sort-section">
              <div className="sort-row">
                <div className="filter-group">
                  <label>정렬 기준:</label>
                  <select
                    value={sessionFilters.sortBy}
                    onChange={(e) => handleFilterChange('sortBy', e.target.value)}
                  >
                    <option value="access_time">접속 시간</option>
                    <option value="tokens">토큰 사용량</option>
                    <option value="cost">비용</option>
                    <option value="browser">브라우저</option>
                    <option value="location">위치</option>
                    <option value="ip_address">IP 주소</option>
                    <option value="language">사용 언어</option>
                    {SESSION_STATUS_COLUMNS.map(({ key, label }) => (
                      <option key={key} value={key}>{label}</option>
                    ))}
                  </select>
                </div>
                <div className="filter-group">
                  <label>정렬 순서:</label>
                  <select
                    value={sessionFilters.sortOrder}
                    onChange={(e) => handleFilterChange('sortOrder', e.target.value)}
                  >
                    <option value="desc">내림차순</option>
                    <option value="asc">오름차순</option>
                  </select>
                </div>
                <div className="filter-actions">
                  <button onClick={() => fetchSessions(1, sessionFilters)} className="apply-btn">적용</button>
                </div>
              </div>
            </div>

            {/* Results Summary */}
            {sessionsPagination && (
              <div className="results-summary">
                <span>
                  📊 분석 기간: {globalDateRange.customMode ? '사용자 지정' : datePresets[globalDateRange.preset]?.label} | 
                  총 {formatNumber(sessionsPagination.total || 0)}개의 세션 (페이지 {sessionsPagination.page || 1}/{sessionsPagination.total_pages || 1})
                </span>
              </div>
            )}

            {/* Bulk actions */}
            {sessions.length > 0 && (
              <div className="bulk-actions">
                <button 
                  onClick={handleSelectAll}
                  className="select-all-btn"
                >
                  {selectedSessions.size === sessions.length ? '전체 해제' : '전체 선택'}
                </button>
                {selectedSessions.size > 0 && (
                  <button 
                    onClick={() => setShowDeleteConfirm(true)}
                    className="bulk-delete-btn"
                    disabled={loading}
                  >
                    선택된 {selectedSessions.size}개 삭제
                  </button>
                )}
              </div>
            )}

            <div className="table-container">
              <table className="sessions-table">
                <thead>
                  <tr>
                    <th className="checkbox-column">
                      <input 
                        type="checkbox" 
                        checked={sessions.length > 0 && selectedSessions.size === sessions.length}
                        onChange={handleSelectAll}
                      />
                    </th>
                    <th onClick={() => handleSort('access_time')} className="sortable">
                      접속 시간 {sessionFilters.sortBy === 'access_time' && (sessionFilters.sortOrder === 'desc' ? '↓' : '↑')}
                    </th>
                    <th onClick={() => handleSort('ip_address')} className="sortable">
                      IP 주소 {sessionFilters.sortBy === 'ip_address' && (sessionFilters.sortOrder === 'desc' ? '↓' : '↑')}
                    </th>
                    <th onClick={() => handleSort('location')} className="sortable">
                      위치 {sessionFilters.sortBy === 'location' && (sessionFilters.sortOrder === 'desc' ? '↓' : '↑')}
                    </th>
                    <th onClick={() => handleSort('browser')} className="sortable">
                      브라우저 {sessionFilters.sortBy === 'browser' && (sessionFilters.sortOrder === 'desc' ? '↓' : '↑')}
                    </th>
                    <th onClick={() => handleSort('tokens')} className="sortable">
                      토큰 사용 {sessionFilters.sortBy === 'tokens' && (sessionFilters.sortOrder === 'desc' ? '↓' : '↑')}
                    </th>
                    <th onClick={() => handleSort('cost')} className="sortable">
                      비용 {sessionFilters.sortBy === 'cost' && (sessionFilters.sortOrder === 'desc' ? '↓' : '↑')}
                    </th>
                    <th onClick={() => handleSort('language')} className="sortable">
                      언어 {sessionFilters.sortBy === 'language' && (sessionFilters.sortOrder === 'desc' ? '↓' : '↑')}
                    </th>
                    {SESSION_STATUS_COLUMNS.map(({ key, label, description }) => (
                      <th key={key} onClick={() => handleSort(key)} className="sortable" title={description}>
                        {label} {sessionFilters.sortBy === key && (sessionFilters.sortOrder === 'desc' ? '↓' : '↑')}
                      </th>
                    ))}
                    <th className="actions-column"></th>
                  </tr>
                </thead>
                <tbody>
                  {sessions.map((session) => (
                    <tr key={session._id}>
                      <td className="checkbox-column">
                        <input 
                          type="checkbox" 
                          checked={selectedSessions.has(session._id)}
                          onChange={() => handleSessionSelect(session._id)}
                        />
                      </td>
                      <td>{formatDate(session.access_time)}</td>
                      <td>{session.ip_address}</td>
                      <td>{session.location?.country} - {session.location?.city}</td>
                      <td>{session.browser_info?.browser}</td>
                      <td>{formatNumber(session.total_tokens_used || 0)}</td>
                      <td>{formatCurrency(session.total_cost || 0)}</td>
                      <td>{session.language_used || 'Unknown'}</td>
                      {SESSION_STATUS_COLUMNS.map(({ key }) => (
                        <td key={key} title={session[`${key}_time`] || ''}>{session[key] ? '✅' : '-'}</td>
                      ))}
                      <td className="actions-column">
                        <button 
                          onClick={() => handleDeleteSession(session._id)}
                          className="delete-btn"
                          disabled={deletingSession === session._id}
                          title="세션 삭제"
                        >
                          {deletingSession === session._id ? '삭제중...' : '🗑️'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {sessionsPagination && sessionsPagination.total_pages > 1 && (
              <div className="pagination">
                <button 
                  onClick={() => handlePageChange(sessionsPagination.page - 1)}
                  disabled={sessionsPagination.page <= 1}
                  className="pagination-btn"
                >
                  이전
                </button>
                <span className="pagination-info">
                  페이지 {sessionsPagination.page} / {sessionsPagination.total_pages}
                </span>
                <button 
                  onClick={() => handlePageChange(sessionsPagination.page + 1)}
                  disabled={sessionsPagination.page >= sessionsPagination.total_pages}
                  className="pagination-btn"
                >
                  다음
                </button>
              </div>
            )}
          </div>
        )}

        {activeTab === 'tokens' && (
          <div className="tokens-section">
            <div className="section-header">
              <h3>토큰 사용량 상세</h3>
              <button onClick={() => exportData('token_usage')} className="export-btn">
                📥 내보내기
              </button>
            </div>
            <div className="table-container">
              <table className="tokens-table">
                <thead>
                  <tr>
                    <th>시간</th>
                    <th>세션 ID</th>
                    <th>모델</th>
                    <th>작업</th>
                    <th>프롬프트 토큰</th>
                    <th>완료 토큰</th>
                    <th>총 토큰</th>
                    <th>비용</th>
                  </tr>
                </thead>
                <tbody>
                  {tokenUsage.map((usage) => (
                    <tr key={usage._id}>
                      <td>{formatDate(usage.timestamp)}</td>
                      <td>{usage.session_id.substring(0, 8)}...</td>
                      <td>{usage.model}</td>
                      <td>{usage.operation}</td>
                      <td>{formatNumber(usage.prompt_tokens || 0)}</td>
                      <td>{formatNumber(usage.completion_tokens || 0)}</td>
                      <td>{formatNumber(usage.total_tokens || 0)}</td>
                      <td>{formatCurrency(usage.total_cost || 0)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'donations' && (
          <div className="sessions-section">
            <div className="section-header">
              <h3>후원 결제 내역</h3>
            </div>

            {donationSummary && (
              <div className="stats-grid">
                {donationSummary.completed_by_currency.length === 0 && (
                  <div className="stat-card">
                    <h3>완료된 후원 금액</h3>
                    <div className="stat-value">{formatDonationAmount(0)}</div>
                  </div>
                )}
                {donationSummary.completed_by_currency.map((item) => (
                  <div key={item.currency} className="stat-card">
                    <h3>완료된 후원 금액 ({item.currency})</h3>
                    <div className="stat-value">{formatDonationAmount(item.total_amount, item.currency)}</div>
                    <div>{formatNumber(item.count)}건</div>
                  </div>
                ))}
                {Object.entries(donationStatusLabels).map(([status, label]) => (
                  <div key={status} className="stat-card">
                    <h3>{label}</h3>
                    <div className="stat-value">{formatNumber(donationSummary.status_counts[status] || 0)}</div>
                  </div>
                ))}
              </div>
            )}

            <div className="sort-section">
              <div className="sort-row">
                <div className="filter-group">
                  <label>결제 상태:</label>
                  <select
                    value={donationFilters.status}
                    onChange={(e) => handleDonationFilterChange('status', e.target.value)}
                  >
                    <option value="">전체</option>
                    {Object.entries(donationStatusLabels).map(([status, label]) => (
                      <option key={status} value={status}>{label}</option>
                    ))}
                  </select>
                </div>
                <div className="filter-group">
                  <label>결제 수단:</label>
                  <select
                    value={donationFilters.paymentMethod}
                    onChange={(e) => handleDonationFilterChange('paymentMethod', e.target.value)}
                  >
                    <option value="">전체</option>
                    {Object.entries(paymentMethodLabels).map(([method, label]) => (
                      <option key={method} value={method}>{label}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            <div className="results-summary">
              <span>
                📊 분석 기간: {globalDateRange.customMode ? '사용자 지정' : datePresets[globalDateRange.preset]?.label} | 
                총 {formatNumber(donationsPagination.total || 0)}건 (페이지 {donationsPagination.page || 1}/{donationsPagination.total_pages || 1})
              </span>
            </div>

            <div className="table-container">
              <table className="sessions-table">
                <thead>
                  <tr>
                    <th>결제 요청 시간</th>
                    <th>승인 시간</th>
                    <th>후원자</th>
                    <th>금액</th>
                    <th>결제 수단</th>
                    <th>유형</th>
                    <th>상태</th>
                    <th>주문 ID</th>
                    <th>거래 ID</th>
                  </tr>
                </thead>
                <tbody>
                  {donations.length === 0 && (
                    <tr>
                      <td colSpan={9}>해당 기간의 후원 결제 내역이 없습니다.</td>
                    </tr>
                  )}
                  {donations.map((donation) => (
                    <tr key={donation._id}>
                      <td>{formatDate(donation.created_at)}</td>
                      <td>{donation.approved_at ? formatDate(donation.approved_at) : '-'}</td>
                      <td>
                        {donation.user_name || '비회원'}
                        {donation.user_email && <div>{donation.user_email}</div>}
                      </td>
                      <td>{formatDonationAmount(donation.amount, donation.currency)}</td>
                      <td>{paymentMethodLabels[donation.payment_method] || donation.payment_method || '-'}</td>
                      <td>{donation.donation_type === 'recurring' ? '정기' : '일시'}</td>
                      <td>{donationStatusLabels[donation.status] || donation.status}</td>
                      <td>{donation.order_id || '-'}</td>
                      <td title={donation.transaction_id || ''}>
                        {!donation.transaction_id ? '-'
                          : donation.transaction_id.length > 12 ? `${donation.transaction_id.substring(0, 12)}...`
                          : donation.transaction_id}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {donationsPagination.total_pages > 1 && (
              <div className="pagination">
                <button 
                  onClick={() => fetchDonations(donationsPagination.page - 1, donationFilters)}
                  disabled={donationsPagination.page <= 1}
                  className="pagination-btn"
                >
                  이전
                </button>
                <span className="pagination-info">
                  페이지 {donationsPagination.page} / {donationsPagination.total_pages}
                </span>
                <button 
                  onClick={() => fetchDonations(donationsPagination.page + 1, donationFilters)}
                  disabled={donationsPagination.page >= donationsPagination.total_pages}
                  className="pagination-btn"
                >
                  다음
                </button>
              </div>
            )}
          </div>
        )}

        {activeTab === 'insights' && (
          <div className="insights-section">
            <div className="section-header">
              <h3>AI 분석 인사이트</h3>
              <div className="insight-actions">
                <button onClick={() => generateInsight('user_behavior')} disabled={loading}>
                  🔍 사용자 행동 분석
                </button>
                <button onClick={() => generateInsight('cost_optimization')} disabled={loading}>
                  💰 비용 최적화 분석
                </button>
                <button onClick={() => generateInsight('conversion_analysis')} disabled={loading}>
                  📈 전환율 분석
                </button>
              </div>
            </div>
            <div className="insights-list">
              {insights.map((insight) => (
                <InsightCard key={insight._id} insight={insight} />
              ))}
            </div>
          </div>
        )}
        
        {activeTab === 'security' && (
          <div className="sessions-section">
            <div className="section-header">
              <h3>비정상 입력 시도 기록</h3>
            </div>

            <div className="sort-section">
              <div className="sort-row">
                <div className="filter-group">
                  <label>IP 영구 차단:</label>
                  <input
                    type="text"
                    value={permanentBlockForm.ip}
                    onChange={(e) => setPermanentBlockForm({ ...permanentBlockForm, ip: e.target.value })}
                    placeholder="IP 주소 (예: 81.171.74.60)"
                  />
                </div>
                <div className="filter-group">
                  <label>사유:</label>
                  <input
                    type="text"
                    value={permanentBlockForm.reason}
                    onChange={(e) => setPermanentBlockForm({ ...permanentBlockForm, reason: e.target.value })}
                    placeholder="선택 입력"
                  />
                </div>
                <div className="filter-actions">
                  <button
                    onClick={() => permanentlyBlockIp(permanentBlockForm.ip, permanentBlockForm.reason)}
                    disabled={blockingIp !== null}
                    className="bulk-delete-btn"
                  >
                    🚫 영구 차단
                  </button>
                </div>
              </div>
              {permanentBlockedIps.length > 0 && (
                <div className="permanent-blocked-list">
                  <strong>영구 차단된 IP ({permanentBlockedIps.length}):</strong>
                  {permanentBlockedIps.map((blocked) => (
                    <span key={blocked.ip_address} className="permanent-blocked-item" title={blocked.reason || ''}>
                      {blocked.ip_address}
                      <button onClick={() => unblockIp(blocked.ip_address)} title="차단 해제">해제</button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            <div className="sort-section">
              <div className="sort-row">
                <div className="filter-group">
                  <label>심각도:</label>
                  <select
                    value={securityFilters.severity}
                    onChange={(e) => setSecurityFilters({ ...securityFilters, severity: e.target.value })}
                  >
                    <option value="">전체</option>
                    {Object.entries(securitySeverityLabels).map(([severity, label]) => (
                      <option key={severity} value={severity}>{label}</option>
                    ))}
                  </select>
                </div>
                <div className="filter-group">
                  <label>IP 주소:</label>
                  <input
                    type="text"
                    value={securityFilters.ip}
                    onChange={(e) => setSecurityFilters({ ...securityFilters, ip: e.target.value })}
                    placeholder="예: 1.2.3.4"
                  />
                </div>
                <div className="filter-actions">
                  <button onClick={() => fetchSecurityData(securityFilters)} className="apply-btn">적용</button>
                </div>
              </div>
            </div>

            <div className="results-summary">
              <span>최근 {formatNumber(securityEvents.length)}건 (최대 200건, 90일 보관)</span>
            </div>

            <div className="table-container">
              <table className="sessions-table">
                <thead>
                  <tr>
                    <th className="security-time-column">감지 시간</th>
                    <th>IP 주소</th>
                    <th>이벤트</th>
                    <th>심각도</th>
                    <th className="nowrap-column">조치</th>
                    <th className="security-time-column" title="요청 1건의 위험 점수 / 15분 내 같은 IP의 누적 점수 (150 이상이면 자동 영구 차단)">점수 (누적)</th>
                    <th>요청</th>
                    <th>사유</th>
                    <th>입력 일부</th>
                    <th className="actions-column">영구 차단</th>
                  </tr>
                </thead>
                <tbody>
                  {securityEvents.length === 0 && (
                    <tr>
                      <td colSpan={10}>기록된 비정상 입력 시도가 없습니다.</td>
                    </tr>
                  )}
                  {securityEvents.map((event) => (
                    <tr key={event._id}>
                      <td className="security-time-column">{event.created_at_kst || formatDate(event.created_at)}</td>
                      <td>{event.ip_address}</td>
                      <td>{event.event_type}</td>
                      <td>{securitySeverityLabels[event.severity] || event.severity}</td>
                      <td className="nowrap-column">
                        {securityActionLabels[event.action] || event.action}
                        {event.block_duration_days ? ` (${event.block_duration_days}일)` : ''}
                      </td>
                      <td className="security-time-column">
                        {event.score ?? '-'}
                        {event.cumulative_score != null && ` (누적 ${event.cumulative_score})`}
                      </td>
                      <td>{event.method} {event.path}</td>
                      <td title={event.detail || ''}>{event.detail || '-'}</td>
                      <td title={event.user_agent || ''}>{event.snippet || '-'}</td>
                      <td className="actions-column">
                        {permanentlyBlockedIpSet.has(event.ip_address) ? (
                          <span className="security-time-column">영구 차단됨</span>
                        ) : (
                          <button
                            onClick={() => permanentlyBlockIp(event.ip_address, `${event.event_type}: ${event.detail || ''}`.slice(0, 200))}
                            disabled={blockingIp !== null}
                            className="delete-btn"
                            title={`${event.ip_address} 영구 차단`}
                          >
                            {blockingIp === event.ip_address ? '차단중...' : '🚫'}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'llmModels' && (
          <LlmModelSettings
            apiBase={`${API_BASE}/llm-models`}
            authHeaders={authHeaders}
            onUnauthorized={redirectToLogin}
          />
        )}

        {/* Delete confirmation modal */}
        {showDeleteConfirm && (
          <div className="modal-overlay">
            <div className="modal">
              <h3>세션 삭제 확인</h3>
              <p>
                선택된 {selectedSessions.size}개의 세션을 삭제하시겠습니까?
                <br />
                <strong>이 작업은 되돌릴 수 없습니다.</strong>
              </p>
              <div className="modal-actions">
                <button 
                  onClick={() => setShowDeleteConfirm(false)}
                  className="cancel-btn"
                  disabled={loading}
                >
                  취소
                </button>
                <button 
                  onClick={handleBulkDelete}
                  className="confirm-delete-btn"
                  disabled={loading}
                >
                  {loading ? '삭제 중...' : '삭제'}
                </button>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'dailyWorship' && (
          <div className="daily-worship-section">
            <h2>📰 오늘의 예배 관리</h2>
            
            {/* Permanent Language Settings */}
            <div className="worship-card">
              <h3>⚙️ 영구 언어 설정</h3>
              <div className="permanent-settings-info">
                <p>
                  <strong>현재 설정 출처:</strong> 
                  <span className={`settings-source ${languageSettingsSource}`}>
                    {languageSettingsSource === 'database' ? '🗄️ 데이터베이스 (영구 설정)' : '📄 config.py (기본값)'}
                  </span>
                </p>
                <p className="help-text">
                  여기서 설정한 언어는 <strong>자동 스케줄 실행 시에도 적용</strong>됩니다.
                </p>
                
                <div className="scheduler-language-selection">
                  <h4>🌍 기본 활성화 언어 선택</h4>
                  
                  <div className="language-selection-controls">
                    <button onClick={selectAllPermanentLanguages} className="select-all-btn" disabled={savingSettings}>
                      전체 선택
                    </button>
                    <button onClick={deselectAllPermanentLanguages} className="deselect-all-btn" disabled={savingSettings}>
                      전체 해제
                    </button>
                    <span className="selected-count">
                      선택됨: {permanentLanguages.length}개
                    </span>
                  </div>
                  
                  <div className="language-grid">
                    {supportedLanguages.map(lang => (
                      <label key={lang.code} className="language-checkbox">
                        <input
                          type="checkbox"
                          checked={permanentLanguages.includes(lang.code)}
                          onChange={() => togglePermanentLanguage(lang.code)}
                          disabled={savingSettings}
                        />
                        <span className="language-label">
                          {lang.country}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
                
                <div className="settings-actions">
                  <button 
                    onClick={savePermanentLanguageSettings} 
                    disabled={savingSettings || permanentLanguages.length === 0}
                    className="save-settings-btn"
                  >
                    {savingSettings ? '저장 중...' : '💾 영구 설정으로 저장'}
                  </button>
                  
                  {languageSettingsSource === 'database' && (
                    <button 
                      onClick={resetPermanentLanguageSettings} 
                      disabled={savingSettings}
                      className="reset-settings-btn"
                    >
                      {savingSettings ? '초기화 중...' : '🔄 설정 초기화 (config.py 기본값)'}
                    </button>
                  )}
                </div>
                
                <div className="settings-info-box">
                  <p>💡 <strong>설명:</strong></p>
                  <ul>
                    <li>여기서 저장한 언어는 <strong>데이터베이스에 영구적으로 저장</strong>됩니다.</li>
                    <li><strong>자동 스케줄 실행</strong> (매일 0시 5분)에도 적용됩니다.</li>
                    <li>"초기화" 클릭 시 config.py의 ENABLED_LANGUAGES 설정을 사용합니다.</li>
                  </ul>
                </div>
              </div>
            </div>
            
            {/* Scheduler Status */}
            <div className="worship-card">
              <h3>⏰ 스케줄러 수동 트리거</h3>
              {schedulerStatus ? (
                <div className="scheduler-info">
                  <p>
                    <strong>스케줄러 상태:</strong> 
                    <span className={schedulerStatus.running ? 'status-running' : 'status-stopped'}>
                      {schedulerStatus.running ? '실행 중 ✅' : '중지됨 ⛔'}
                    </span>
                  </p>
                  {schedulerStatus.next_run && (
                    <p><strong>다음 자동 실행:</strong> {new Date(schedulerStatus.next_run).toLocaleString('ko-KR')}</p>
                  )}
                  
                  {/* Language Selection for Scheduler */}
                  <div className="scheduler-language-selection">
                    <h4>🌍 생성할 언어 선택</h4>
                    <p className="help-text">
                      언어를 선택하지 않으면 활성화된 모든 언어로 생성됩니다.
                    </p>
                    
                    <div className="language-selection-controls">
                      <button onClick={selectAllLanguages} className="select-all-btn" disabled={worshipGenerating}>
                        전체 선택
                      </button>
                      <button onClick={deselectAllLanguages} className="deselect-all-btn" disabled={worshipGenerating}>
                        전체 해제
                      </button>
                      <span className="selected-count">
                        선택됨: {selectedSchedulerLanguages.length}개
                      </span>
                    </div>
                    
                    <div className="language-grid">
                      {supportedLanguages.map(lang => (
                        <label key={lang.code} className="language-checkbox">
                          <input
                            type="checkbox"
                            checked={selectedSchedulerLanguages.includes(lang.code)}
                            onChange={() => toggleSchedulerLanguage(lang.code)}
                            disabled={worshipGenerating}
                          />
                          <span className="language-label">
                            {lang.country}
                            {lang.enabled && <span className="enabled-badge">✓ 활성화됨</span>}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                  
                  <button 
                    onClick={triggerScheduler} 
                    disabled={worshipGenerating}
                    className="trigger-btn"
                  >
                    {worshipGenerating ? '실행 중...' : 
                      selectedSchedulerLanguages.length > 0 
                        ? `🚀 선택된 ${selectedSchedulerLanguages.length}개 언어 생성`
                        : '🚀 활성화된 모든 언어 생성'
                    }
                  </button>
                  
                  {selectedSchedulerLanguages.length > 0 && (
                    <p className="cost-warning">
                      ⚠️ 예상 비용: ~${(selectedSchedulerLanguages.length * 0.50).toFixed(2)}, 
                      소요 시간: {selectedSchedulerLanguages.length * 5}-{selectedSchedulerLanguages.length * 10}분
                    </p>
                  )}
                </div>
              ) : (
                <p>스케줄러 상태를 불러오는 중...</p>
              )}
            </div>

            {/* Language Selection */}
            <div className="worship-card">
              <h3>🌍 언어 선택</h3>
              <div className="language-selector">
                <label htmlFor="language-select">예배 생성 언어:</label>
                <select 
                  id="language-select"
                  value={selectedLanguage} 
                  onChange={(e) => setSelectedLanguage(e.target.value)}
                  disabled={worshipGenerating}
                >
                  {languages.map(lang => (
                    <option key={lang.code} value={lang.code}>
                      {lang.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Generate Single Language */}
            <div className="worship-card">
              <h3>📝 단일 언어 예배 생성</h3>
              <p>선택한 언어로 오늘의 사회적 이슈 5개에 대한 예배를 생성합니다.</p>
              <p className="cost-warning">⚠️ 예상 비용: ~$0.50, 소요 시간: 5-10분</p>
              <button 
                onClick={generateDailyWorship} 
                disabled={worshipGenerating}
                className="generate-btn"
              >
                {worshipGenerating ? '생성 중...' : `${selectedLanguage.toUpperCase()} 예배 생성`}
              </button>
            </div>

            {/* Generate All Languages */}
            <div className="worship-card">
              <h3>🌏 전체 언어 예배 생성</h3>
              <p>모든 언어(12개)로 오늘의 예배를 생성합니다.</p>
              <p className="cost-warning">⚠️ 예상 비용: $6-11, 소요 시간: 30-60분</p>
              <button 
                onClick={generateAllLanguages} 
                disabled={worshipGenerating}
                className="generate-all-btn"
              >
                {worshipGenerating ? '생성 중...' : '전체 언어 예배 생성'}
              </button>
            </div>

            {/* Custom Issue */}
            <div className="worship-card">
              <h3>✍️ 사용자 정의 이슈</h3>
              <p>특정 상황에 대한 예배를 직접 생성할 수 있습니다.</p>
              <div className="custom-issue-form">
                <div className="form-group">
                  <label>이슈 제목:</label>
                  <input 
                    type="text"
                    value={customIssueTitle}
                    onChange={(e) => setCustomIssueTitle(e.target.value)}
                    placeholder="예: 경제 위기 극복을 위한 기도"
                    disabled={worshipGenerating}
                  />
                </div>
                <div className="form-group">
                  <label>이슈 설명:</label>
                  <textarea 
                    value={customIssueDescription}
                    onChange={(e) => setCustomIssueDescription(e.target.value)}
                    placeholder="이슈에 대한 자세한 설명을 입력하세요..."
                    rows={4}
                    disabled={worshipGenerating}
                  />
                </div>
                <button 
                  onClick={generateCustomWorship} 
                  disabled={worshipGenerating || !customIssueTitle || !customIssueDescription}
                  className="generate-btn"
                >
                  {worshipGenerating ? '생성 중...' : '사용자 정의 예배 생성'}
                </button>
              </div>
            </div>

            {/* Result Display */}
            {worshipResult && (
              <div className={`result-card ${worshipResult.success ? 'success' : 'error'}`}>
                <h3>{worshipResult.success ? '✅ 성공' : '❌ 실패'}</h3>
                <p>{worshipResult.message}</p>
                {worshipResult.worshipId && (
                  <p><strong>예배 ID:</strong> {worshipResult.worshipId}</p>
                )}
                {worshipResult.issuesCount && (
                  <p><strong>생성된 이슈:</strong> {worshipResult.issuesCount}개</p>
                )}
                {worshipResult.results && (
                  <div className="all-results">
                    <h4>상세 결과:</h4>
                    {worshipResult.results.map((result, index) => (
                      <div key={index} className="language-result">
                        <strong>{result.language}:</strong> 
                        {result.result.success ? 
                          ` ✅ 성공 (${result.result.issues_count}개 이슈)` : 
                          ` ❌ 실패 - ${result.result.error}`
                        }
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Error Display */}
            {error && activeTab === 'dailyWorship' && (
              <div className="error-card">
                <h3>❌ 오류</h3>
                <p>{error}</p>
              </div>
            )}

            {/* Info Box */}
            <div className="info-box">
              <h4>ℹ️ 사용 안내</h4>
              <ul>
                <li>스케줄러는 매일 오전 0시 5분(UTC)에 자동으로 <strong>활성화된 언어</strong>의 예배를 생성합니다.</li>
                <li>환경 변수 <code>ENABLED_LANGUAGES</code>로 생성할 언어를 설정할 수 있습니다 (기본값: 모든 언어).</li>
                <li>수동 트리거 시 특정 언어만 선택하여 생성할 수 있습니다.</li>
                <li>단일 언어 생성은 테스트 및 긴급 상황에 사용하세요.</li>
                <li>사용자 정의 이슈는 특별한 상황이나 이벤트를 위해 사용할 수 있습니다.</li>
                <li>생성된 예배는 MongoDB에 저장되며 사용자는 무작위로 하나를 선택하여 예배를 드립니다.</li>
              </ul>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
